import { Button, Checkbox, Input, InputNumber, Modal, Space, Table, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React from "react";
import { computeIngredientPrice, computeRecipePrice } from "../calculations";
import type { DrinkRecipe, Ingredient } from "../types";
import type { FestivalCalcStore } from "../store";

interface Props {
  open: boolean;
  recipe: DrinkRecipe | null;
  store: FestivalCalcStore;
  onClose: () => void;
}

export const RecipeModal = observer(({ open, recipe, store, onClose }: Props) => {
  if (!recipe?.objectId) return null;

  const recipeId = recipe.objectId;
  const ingredients = store.ingredientsByRecipe.get(recipeId) ?? [];
  const recipePrice = computeRecipePrice(ingredients);

  function updateIngredient(objectId: string, patch: Partial<Ingredient>) {
    store.updateIngredient(recipeId, objectId, patch);
  }

  const columns = [
    {
      title: "Aktiv",
      dataIndex: "active",
      width: 55,
      render: (_: any, item: Ingredient) => (
        <Checkbox
          checked={item.active}
          onChange={(e) => item.objectId && updateIngredient(item.objectId, { active: e.target.checked })}
        />
      ),
    },
    {
      title: "Item",
      dataIndex: "name",
      render: (_: any, item: Ingredient) => (
        <Input
          size="small"
          value={item.name}
          placeholder="Zutat"
          onChange={(e) => item.objectId && updateIngredient(item.objectId, { name: e.target.value })}
          style={{ minWidth: 120 }}
        />
      ),
    },
    {
      title: "Einheiten",
      dataIndex: "quantity",
      width: 90,
      render: (_: any, item: Ingredient) => (
        <InputNumber
          size="small"
          min={1}
          step={1}
          value={item.quantity}
          onChange={(v) => item.objectId && updateIngredient(item.objectId, { quantity: v ?? 1 })}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "Einheit",
      dataIndex: "unit",
      width: 90,
      render: (_: any, item: Ingredient) => (
        <Input
          size="small"
          value={item.unit}
          placeholder="ml / l / Fl."
          onChange={(e) => item.objectId && updateIngredient(item.objectId, { unit: e.target.value })}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "Gebindegröße",
      dataIndex: "packageSize",
      width: 110,
      render: (_: any, item: Ingredient) => (
        <InputNumber
          size="small"
          min={0.0001}
          step="any"
          value={item.packageSize}
          onChange={(v) => item.objectId && updateIngredient(item.objectId, { packageSize: v ?? 1 })}
          style={{ width: 90 }}
        />
      ),
    },
    {
      title: "Gebindepreis",
      dataIndex: "packagePrice",
      width: 110,
      render: (_: any, item: Ingredient) => (
        <InputNumber
          size="small"
          min={0}
          step={0.01}
          value={item.packagePrice}
          onChange={(v) => item.objectId && updateIngredient(item.objectId, { packagePrice: v ?? 0 })}
          style={{ width: 90 }}
          addonAfter="€"
        />
      ),
    },
    {
      title: "Preis",
      key: "price",
      width: 90,
      render: (_: any, item: Ingredient) => (
        <Typography.Text type={item.active ? undefined : "secondary"}>
          {computeIngredientPrice(item).toFixed(2)} €
        </Typography.Text>
      ),
    },
    {
      title: "",
      key: "delete",
      width: 40,
      render: (_: any, item: Ingredient) => (
        <Button
          type="text"
          danger
          size="small"
          onClick={() => item.objectId && store.deleteIngredient(recipeId, item.objectId)}
        >
          ✖
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title={
        <Space>
          <Typography.Text>Zutaten für</Typography.Text>
          <Input
            value={recipe.name}
            onChange={(e) => recipe.objectId && store.updateRecipe(recipe.objectId, { name: e.target.value })}
            style={{ width: 200 }}
            size="small"
          />
        </Space>
      }
      onCancel={onClose}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography.Text strong>
            Summe:{" "}
            {recipePrice !== null ? `${recipePrice.toFixed(2)} €` : "— (keine aktiven Zutaten)"}
          </Typography.Text>
          <Button type="primary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
      width={800}
    >
      <Table
        dataSource={ingredients}
        columns={columns}
        rowKey={(r) => r.objectId ?? Math.random().toString()}
        pagination={false}
        size="small"
        style={{ marginBottom: 8 }}
      />
      <Button type="dashed" onClick={() => store.addIngredient(recipeId)}>
        + Zutat hinzufügen
      </Button>
    </Modal>
  );
});
